import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ShippingAndDeliverySettings = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "key-elements",
        "market-selection",
        "shipping-rates",
        "conditions-restrictions",
        "additional-options",
        "save-changes",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Shipping & Delivery Settings | xPage Help Center"
        description="Configure your store's shipping and delivery options with Xpage. Learn how to manage markets, shipping rates, conditions, additional options, and save changes to optimize delivery for your customers."
        keywords="xPage shipping settings, delivery options, shipping rates, market selection, shipping conditions, ecommerce shipping configuration"
        canonical="https://help.xpage.ai/help/settings/Shipping%20%26%20Delivery%20Settings"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Shipping & Delivery Settings
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage provides a flexible and professional shipping configuration
            system, allowing you to define how products reach your customers
            while keeping costs transparent and manageable. With these settings,
            you can tailor shipping strategies to match your business model and
            target markets.
          </p>
        </header>

        {/* Key Elements Section */}
        <section id="key-elements" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Key Elements
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              {/* Market Selection */}
              <div id="market-selection" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  1. Market Selection
                </h3>
                <p className="text-sm text-systemLabel">
                  Select the countries or regions where your store ships. Xpage
                  allows you to manage multiple markets, ensuring that shipping
                  rates, delivery times, and policies can be customized based on
                  location.
                </p>
              </div>

              {/* Shipping Rates */}
              <div id="shipping-rates" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  2. Shipping Rates
                </h3>
                <p className="text-sm text-systemLabel">
                  Set shipping costs with full flexibility:
                </p>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          By Order Value
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Define shipping fees based on the total amount of the
                        order. For example, offer free shipping for orders above
                        a certain threshold.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          By Weight (kg)
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Configure shipping rates according to product weight.
                        This is ideal for stores selling items with varying
                        sizes or weights.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-sm text-systemLabel">
                  Xpage provides pre-set rules and examples, making it easy to
                  quickly configure your rates without manual calculations.
                </p>
              </div>

              {/* Conditions & Restrictions */}
              <div id="conditions-restrictions" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  3. Conditions & Restrictions
                </h3>
                <p className="text-sm text-systemLabel">
                  You can apply conditions to control when specific shipping
                  rates apply:
                </p>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Value/Weight Limits
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Minimum and maximum order value or weight.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Market-Specific Rules
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Market-specific rules for different countries or
                        regions.
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Free Shipping Thresholds
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Free shipping thresholds to encourage larger orders.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-sm text-systemLabel">
                  These options help you optimize delivery strategies and manage
                  costs efficiently while improving customer satisfaction.
                </p>
              </div>

              {/* Additional Options */}
              <div id="additional-options" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  4. Additional Options
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Default Shipping Rate
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Set a base rate for markets not explicitly defined.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Optional Carrier Selection
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        If applicable, allow customers to choose their preferred
                        shipping carrier at checkout.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Save Changes */}
              <div id="save-changes" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  5. Save Changes
                </h3>
                <p className="text-sm text-systemLabel">
                  All modifications are saved by clicking the Save button at the
                  bottom of the page. Xpage ensures your settings are applied
                  immediately, providing a seamless experience for both
                  merchants and customers.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#key-elements"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-elements"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key Elements
              </a>
            </li>
            <li>
              <a
                href="#market-selection"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "market-selection"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Market Selection
              </a>
            </li>
            <li>
              <a
                href="#shipping-rates"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "shipping-rates"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Shipping Rates
              </a>
            </li>
            <li>
              <a
                href="#conditions-restrictions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "conditions-restrictions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Conditions & Restrictions
              </a>
            </li>
            <li>
              <a
                href="#additional-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "additional-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Additional Options
              </a>
            </li>
            <li>
              <a
                href="#save-changes"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "save-changes"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Save Changes
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ShippingAndDeliverySettings;
